import React, { useEffect, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import { Plugin } from 'chart.js';
import ChartDataLabels, { Context } from 'chartjs-plugin-datalabels';
import { ScaleOptions, DataSet, Tooltip, LabelPosition } from './types';
import { summaryLabelsPlugin } from './plugins';
import { HIDE_LABELS_DATASET_SIZE } from './constants';
import { calculateSpaceByCharacter } from '../../common/util';
import { ChangeType } from '../../types';
import { ContextType, renderTooltip } from './tooltip';
import { getChartHeight } from './utils';

export const CHANGE_TYPE_AND_LABEL_POSITION_MAP = {
  [ChangeType.absolute]: LabelPosition.TOP,
  [ChangeType.percent]: LabelPosition.BOTTOM,
};

export type StackedScaleOptions = ScaleOptions & {
  stacked: boolean;
  useMax?: number;
};

export type StackedDataSet = DataSet & {
  stack?: string;
};

export type StackedData = {
  labels: (string[] | string)[];
  sets: StackedDataSet[];
  summary?: DataSet;
  additionalSummary?: DataSet;
};

export type StackedBarChartOptions = {
  xScale?: ScaleOptions;
  yScale?: StackedScaleOptions;
  data?: StackedData;
  tooltip?: Tooltip;
  changesOnLastStack?: boolean;
  compactView?: boolean;
};

type Props = {
  options: StackedBarChartOptions;
  trackTooltipEvent: () => void;
  canvasId?: string;
  isMobile: boolean;
  isChartFullView: boolean;
  chartHeightOffset?: number;
};
function StackedBarChart({
  options = {},
  trackTooltipEvent,
  canvasId,
  isMobile,
  isChartFullView,
  chartHeightOffset = 0,
}: Readonly<Props>) {
  const [width, setWidth] = useState<string>('176px');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hoverTimeoutRef = useRef();
  const chartRef = useRef<Chart | null>(null);
  const optionsRef = useRef<string>(null);
  const [hasAdditionalSummary, setHasAdditionalSummary] = useState<boolean>(false);

  /**
   * Return the longests label from the changes array
   * @param sets - the sets within the chart data
   * @returns the longest change label
   */
  const getLongestChangeLabel = (sets: any[]): string => {
    return sets.reduce((acc, set) => {
      if (!set.changes) {
        return acc;
      }
      const longestChange = set.changes.formatted.reduce((acc: string, change: string | null) => {
        if (change && change.length > acc.length) {
          return change;
        }
        return acc;
      }, '');
      if (longestChange.length > acc.length) {
        return longestChange;
      }
      return acc;
    }, '');
  };

  useEffect(() => {
    const { data } = options;
    if (!canvasRef.current || !data || optionsRef.current === JSON.stringify(options)) {
      return;
    }

    optionsRef.current = JSON.stringify(options);

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    const {
      data: { sets, summary, additionalSummary },
      changesOnLastStack,
      compactView,
    } = options;

    let changeLabelSpace = 0;
    if (compactView) {
      setWidth('100%');
    } else {
      let calculatedWidth = sets[0]?.values.raw.length * 88;
      if (changesOnLastStack) {
        changeLabelSpace = calculateSpaceByCharacter(getLongestChangeLabel(sets));
        calculatedWidth += changeLabelSpace;
      }
      setWidth(`${calculatedWidth}px`);
    }
    const hideAllLabels = compactView || sets[0]?.values.raw.length >= HIDE_LABELS_DATASET_SIZE;

    const plugins: Plugin[] = [ChartDataLabels];
    if (summary && !hideAllLabels) {
      plugins.push(summaryLabelsPlugin(summary, CHANGE_TYPE_AND_LABEL_POSITION_MAP[summary.changeType]));
    }
    if (additionalSummary && !hideAllLabels) {
      setHasAdditionalSummary(true);
      const summaryLabel = summaryLabelsPlugin(
        additionalSummary,
        CHANGE_TYPE_AND_LABEL_POSITION_MAP[additionalSummary.changeType]
      );
      plugins.push(summaryLabel);
    }
    const timeoutId = hoverTimeoutRef.current;
    //configure chart settings
    configChart(plugins, hideAllLabels, changeLabelSpace);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [options, trackTooltipEvent]);

  const configChart = (plugins, hideAllLabels, changeLabelSpace) => {
    const {
      xScale,
      yScale,
      data: { labels, sets, summary, additionalSummary },
      changesOnLastStack,
      tooltip,
    } = options;

    chartRef.current = new Chart(canvasRef.current, {
      plugins: plugins,
      type: 'bar',
      data: {
        labels: labels,
        datasets: [...sets].reverse().map(set => ({
          label: set.label,
          data: set.values.raw,
          borderColor: set.values.fillColor,
          backgroundColor: set.values.fillColor,
          stack: set.stack,
          datalabels: {
            labels: {
              value: {
                color: set.values.textColor,
                formatter: (_: unknown, ctx: Context) => set.values.formatted[ctx.dataIndex],
                font: {
                  size: 12,
                },
                display: (ctx: Context) => {
                  return handleLabelValueDisplay(ctx, hideAllLabels);
                },
                clamp: true,
              },
              changes: {
                display: (ctx: Context) => {
                  return handleLabelChangesDisplay(ctx, hideAllLabels, set, changesOnLastStack);
                },
                color: (changesOnLastStack && set.changes?.textColor) || [],
                formatter: (_: unknown, ctx: Context) => set.changes?.formatted[ctx.dataIndex] || '',
                font: {
                  size: 12,
                },
                align: 'right',
                anchor: 'center',
                offset: 40,
              },
            },
          },
        })),
      },
      options: {
        responsive: true,
        onHover: (_event, _activeElements, chart) => {
          onHoverActivity(chart, hoverTimeoutRef);
        },
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: !summary ? 0 : 65,
            right: changeLabelSpace,
            bottom: additionalSummary ? 65 : 0,
          },
        },
        plugins: {
          tooltip: {
            enabled: false, // Disable default tooltip rendering
            external: function (context: ContextType) {
              //render custom tooltip
              renderTooltip(context);
            },
            position: 'nearest',
            callbacks: {
              label: item => {
                return handleTooltipLabelCallback(item, tooltip);
              },
            },
          },
          legend: {
            display: false,
          },
        },
        scales: {
          x: {
            stacked: true,
            border: {
              display: false,
            },
            grid: {
              display: false,
            },
            title: {
              display: xScale?.title !== undefined,
              text: xScale?.title || '',
            },
            ticks: {
              callback: function (value: string | number, index: number) {
                return handleXTicksCallback(value, index, hideAllLabels, xScale, labels);
              },
              autoSkip: false,
              maxRotation: 0,
            },
          },
          y: {
            stacked: yScale?.stacked,
            border: {
              display: false,
            },
            grid: {
              display: hideAllLabels,
            },
            title: {
              display: yScale?.title !== undefined,
              text: yScale?.title || '',
              align: 'end',
            },
            beginAtZero: true,
            ticks: {
              display: hideAllLabels,
              callback: function (value: string | number) {
                return handleYTicksCallback(value, yScale);
              },
            },
            grace: -1,
            min: 0,
            max: yScale?.useMax,
          },
        },
      },
    });
  };

  const calHeight = ctx => {
    const chart = ctx.chart;
    const yScale = chart.scales['y'];
    const dataset = chart.data.datasets[ctx.datasetIndex];
    const value = dataset.data[ctx.dataIndex];

    // For stacked bars, we need to calculate the base value
    let baseValue = 0;
    for (let i = 0; i < ctx.datasetIndex; i++) {
      baseValue += chart.data.datasets[i].data[ctx.dataIndex] ?? 0;
    }

    const y1 = yScale.getPixelForValue(baseValue);
    const y2 = yScale.getPixelForValue(baseValue + value);
    const height = Math.abs(y2 - y1);
    return height >= 12;
  };

  const handleLabelChangesDisplay = (ctx, hideAllLabels, lSet, changesOnLastStack) => {
    if (hideAllLabels || !changesOnLastStack || !lSet.changes) {
      return false;
    }
    return calHeight(ctx);
  };

  const handleLabelValueDisplay = (ctx, hideAllLabels) => {
    if (hideAllLabels) {
      return false;
    }
    return calHeight(ctx);
  };

  const handleTooltipLabelCallback = (item, tooltip) => {
    let formattedValue = item.formattedValue;
    if (tooltip?.valueFormatter) {
      formattedValue = tooltip?.valueFormatter(Number(item.raw));
    }
    return `${item.dataset.label}: ${formattedValue}`;
  };

  const handleYTicksCallback = (value: string | number, yScale) => {
    if (yScale?.formatter) {
      return yScale.formatter(value);
    } else {
      return value;
    }
  };

  const handleXTicksCallback = (value: string | number, index: number, hideAllLabels, xScale, labels) => {
    if (hideAllLabels && index % 4 !== 0) {
      return '';
    }

    if (xScale?.formatter) {
      return xScale.formatter(value);
    } else {
      return labels[index];
    }
  };

  const onHoverActivity = (chart, hoverTimeoutRef) => {
    clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      const activeElements = chart.getActiveElements();
      if (activeElements.length > 0) {
        trackTooltipEvent();
      }
    }, 1000);
  };

  return (
    <div
      data-test-id="stacked-bar-chart"
      style={{ width, height: getChartHeight(isMobile, isChartFullView, hasAdditionalSummary, chartHeightOffset) }}
    >
      <canvas id={canvasId} className="h-full" ref={canvasRef} />
    </div>
  );
}

export { StackedBarChart };
